import React from 'react';
import { useStyles, cx } from '../../../lib/styles';
import { Brand, Color } from '../../../lib/theme';

import buttonStyles from './Button_styles';

export interface ButtonProps {
  /**
   * The brand to use for the button colouring.
   *
   * @default 'sas'
   */
  brand?: Brand;

  /**
   * The colour to use for the button background.
   * For inverted buttons, this is the border and text colour.
   *
   * @default 'primary'
   */
  color?: Color;

  /** The label for screen readers. */
  ariaLabel?: string;

  /** Any additional classnames to add to the container element. */
  className?: string;

  /** The rel for the button (if it has a href). */
  rel?: string;

  /** The target for the button (if it has a href). */
  target?: string;

  /* The URL to link the button to. */
  href?: string;

  /**
   * The type of the button.
   * Set this to `submit` in order to submit forms on click.
   *
   * @default 'button'
   */
  type?: 'button' | 'submit';

  /** Whether the button should fill the space provided to it. */
  block?: boolean;

  /**
   * Whether the button should be inverted. If enabled, the button
   * will have a transparent background with a coloured border and text.
   *
   * @default false
   */
  inverted?: boolean;

  /** The event listener for when the button is clicked. */
  onClick?: (event: any) => any;

  /**
   * Whether the button should appear disabled.
   * Note that the `onClick` listener will still trigger with this turned on.
   *
   * @default false
   */
  disabled?: boolean;

  /** The children to show inside the button. */
  children: React.ReactNode;
}

/**
 * A button for driving users to complete an action.
 */
export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>((props, ref) => {
  const {
    type = 'button',
    rel,
    target,
    href,
    disabled,
    children,
    ariaLabel,
    className,
    onClick,
  } = props;
  const styles = useStyles(buttonStyles(props));
  const Container = href ? 'a' : 'button';
  const containerProps = href ? { href, rel, target } : { disabled, type };

  return (
    <Container
      {...containerProps}
      ref={ref as any}
      aria-label={ariaLabel}
      role={'button'}
      onClick={onClick}
      className={cx('Button', styles.container, className)}
    >
      {children}
    </Container>
  );
});

export default Button;
